/**
 * @deprecated Use DS typography classes directly (`text-heading-*`, `text-body-*`).
 * These wrapper components predate the design system and will be removed.
 * Note: `weight="bold"` is now identical to `weight="regular"` for heading
 * components (both map to font-semibold — font-bold is forbidden in the DS).
 */
import { cn } from '@/lib/utils';

type FontWeight = 'regular' | 'bold' | 'light';

interface TextProps {
  weight?: FontWeight;
}

const headerFontWeightMap: Record<FontWeight, string> = {
  regular: 'font-semibold',
  bold: 'font-semibold',
  light: 'font-medium',
};

const paraFontWeightMap: Record<FontWeight, string> = {
  regular: 'font-medium',
  bold: 'font-semibold',
  light: 'font-medium',
};

// Heading Components
export function H1({
  className,
  weight = 'regular',
  ...props
}: React.ComponentProps<'h1'> & TextProps) {
  return (
    <h1 className={cn('text-heading-24', headerFontWeightMap[weight], className)} {...props} />
  );
}

export function H2({
  className,
  weight = 'regular',
  ...props
}: React.ComponentProps<'h2'> & TextProps) {
  return (
    <h2 className={cn('text-heading-20', headerFontWeightMap[weight], className)} {...props} />
  );
}

export function H3({
  className,
  weight = 'regular',
  ...props
}: React.ComponentProps<'h3'> & TextProps) {
  return <h3 className={cn('text-l', headerFontWeightMap[weight], className)} {...props} />;
}

export function H4({
  className,
  weight = 'regular',
  ...props
}: React.ComponentProps<'h4'> & TextProps) {
  return <h4 className={cn('text-ml', headerFontWeightMap[weight], className)} {...props} />;
}

export function H5({
  className,
  weight = 'regular',
  ...props
}: React.ComponentProps<'h5'> & TextProps) {
  return <h5 className={cn('text-m', headerFontWeightMap[weight], className)} {...props} />;
}

export function H6({
  className,
  weight = 'regular',
  ...props
}: React.ComponentProps<'h6'> & TextProps) {
  return <h6 className={cn('text-body-14', headerFontWeightMap[weight], className)} {...props} />;
}

// Paragraph Components
export function P1({
  className,
  weight = 'regular',
  ...props
}: React.ComponentProps<'p'> & TextProps) {
  return <p className={cn('text-m', paraFontWeightMap[weight], className)} {...props} />;
}

export function P2({
  className,
  weight = 'regular',
  ...props
}: React.ComponentProps<'p'> & TextProps) {
  return <p className={cn('text-body-14', paraFontWeightMap[weight], className)} {...props} />;
}

export function P3({
  className,
  weight = 'regular',
  ...props
}: React.ComponentProps<'p'> & TextProps) {
  return <p className={cn('text-s', paraFontWeightMap[weight], className)} {...props} />;
}

export function P4({
  className,
  weight = 'regular',
  ...props
}: React.ComponentProps<'p'> & TextProps) {
  return <p className={cn('text-body-12', paraFontWeightMap[weight], className)} {...props} />;
}

export function P5({
  className,
  weight = 'regular',
  ...props
}: React.ComponentProps<'p'> & TextProps) {
  return <p className={cn('text-2xs', paraFontWeightMap[weight], className)} {...props} />;
}
